import { useCallback } from "react";
import { RecurrenceModel } from "app/services/api/types/calendar/models/RecurrenceModel";
import { FrequencyEnums } from "app/services/api/types/calendar/enums/FrequencyEnums";
import { CreateCalendarEventForm } from "app/services/api/types/calendar/requests/CreateCalendarEventForm";
import { parse, parseISO } from "date-fns";

export function useRecurrenceRuleBuilder() {
  const buildRecurrenceRule = useCallback(
    (appointmentData: CreateCalendarEventForm): RecurrenceModel | null => {
      const recurrenceData = appointmentData.recurrence_rule;
      const frequency = recurrenceData?.frequency?.length
        ? recurrenceData.frequency[0]
        : null;

      if (!frequency || !recurrenceData || frequency === FrequencyEnums.NEVER) {
        return null;
      }

      const rule: Partial<RecurrenceModel> = {
        frequency,
        end_date:
          appointmentData.endsOption === "date"
            ? recurrenceData.end_date
            : null,
        max_occurrences:
          appointmentData.endsOption === "occurrences"
            ? recurrenceData.max_occurrences
            : null,
      };

      switch (frequency) {
        case FrequencyEnums.DAILY: {
          const daily = recurrenceData.daily;
          let interval = daily?.interval || 1;
          let weekDay = daily?.week_day || [];

          if (daily?.option === "weekday") {
            interval = 1;
            weekDay = [1, 2, 3, 4, 5];
          }

          return {
            ...rule,
            interval,
            week_day: weekDay,
          };
        }

        case FrequencyEnums.WEEKLY: {
          const weekly = recurrenceData.weekly;
          return {
            ...rule,
            interval: weekly?.interval || 1,
            week_day: weekly?.week_day || [],
          };
        }

        case FrequencyEnums.MONTHLY: {
          const monthly = recurrenceData.monthly;
          if (!monthly) {
            throw new Error("Monthly recurrence should not be null");
          }
          const option = monthly?.option;
          const day = parseISO(appointmentData.start_date).getDate();

          if (option === "specific_day") {
            return {
              ...rule,
              interval: monthly.interval,
              by_month_day: day,
            };
          } else if (option === "weekday_position") {
            if (!monthly.week_day) {
              throw new Error();
            }
            return {
              ...rule,
              interval: monthly.interval,
              week_day: [Number(monthly.week_day)],
              by_set_position: monthly?.by_set_position ?? 1,
            };
          }
          return rule as RecurrenceModel;
        }

        case FrequencyEnums.YEARLY: {
          const yearly = recurrenceData.yearly;
          if (!yearly) {
            throw new Error("Yearly recurrence should not be null");
          }

          const option = yearly.option;
          const date = parseISO(appointmentData.start_date);
          const month = date.getMonth() + 1;

          if (option === "specific_day_of_the_month") {
            const day = date.getDate();
            return {
              ...rule,
              interval: yearly.interval,
              by_month: month,
              by_month_day: day,
            };
          } else if (option === "day_position_of_the_month") {
            return {
              ...rule,
              interval: yearly.interval,
              by_set_position: yearly.by_set_position ?? 1,
              week_day: [Number(yearly.week_day)],
              by_month: month,
            };
          }
          return rule as RecurrenceModel;
        }

        default:
          throw new Error("Recurrence builder should not be default.");
      }
    },
    [],
  );

  return { buildRecurrenceRule };
}
